import React, {useState} from 'react'
import GridContainer from 'component/material/GridContainer'
import GridItem from 'component/material/GridItem'
import {Typography} from '@material-ui/core'
import {Trans} from '@lingui/macro'
import {ReactComponent as Stepka} from 'style/asset/stepka.svg'
import {ReactComponent as Klada} from 'style/asset/klada.svg'
import withStyles from '@material-ui/core/styles/withStyles'
import shiftDetailStyle from 'component/MapLayoutPage/shiftDetail/shiftDetailStyle'
import PropTypes from 'prop-types'
import cx from 'classnames'
import {Field, Form} from 'react-final-form'
import SelectInput from 'component/field/SelectInput'
import {required} from 'helper/validations'
import {bindActionCreators, compose} from 'redux'
import {connect} from 'react-redux'
import BoxFullWidth from 'component/material/BoxFullWidth'
import TextInput from 'component/field/TextInput'
import PrimaryButton from 'component/material/PrimaryButton'
import {
  createTransport,
  createTransportInStorage,
  getShifts,
  getShiftsFromStorage,
} from 'redux/action/shiftAction'
import {fireErrorToast, fireSuccessToast, fireWarningToast} from 'helper/functions'
import Box from '@material-ui/core/Box'
import InputAdornment from '@material-ui/core/InputAdornment'
import {CUBIC_METERS_TO_PRM} from 'helper/constants'

const NewTransportDialog = (props) => {
  const {
    classes,
    currentShift,
    onClose,
    locationsList,
    createTransport,
    getShifts,
    locationId,
    contractId,
    getShiftsFromStorage,
    isManager,
    shiftsLoading,
  } = props

  const [materialType, setMaterialType] = useState(isManager ? 'RAW' : 'PROCESSED')

  const setActiveTransportMaterial = (type) => (e) => {
    setMaterialType(type)
  }

  const getMaterialUnits = () => (materialType === 'RAW' ? 'm³' : 'prm')

  const prepareTransportData = (values) => {
    const data = {
      delivery_note: values.delivery_note,
      netto: Number(values.netto) / (materialType === 'RAW' ? 1 : CUBIC_METERS_TO_PRM),
      material: materialType,
      destination: {
        location_id: values.destination,
        location_name: locationsList.find((loc) => loc.id === values.destination)?.name,
      },
      unique_id: Math.random().toString(36).substring(2, 15),
    }
    if (currentShift.isOffline) {
      data.isOffline = true
      data.offlineId = currentShift.offlineId
      console.log('Adding transport to shift with offlineId ' + data.offlineId)
    }
    return data
  }

  const handleSubmit = (values) => {
    const transportData = prepareTransportData(values)
    createTransport(locationId, contractId, currentShift.id, transportData)
      .then((res) => {
        fireSuccessToast(<Trans>Transport added.</Trans>)
      })
      .catch((err) => {
        if (err.message === 'Network Error') {
          createTransportInStorage(locationId, contractId, {
            ...transportData,
            transportOfflineId: err.transportOfflineId,
          })
          getShiftsFromStorage(locationId, contractId)
          fireWarningToast(<Trans>Offline mode, data will be synced later</Trans>)
        } else {
          fireErrorToast(<Trans>Adding transport failed.</Trans>)
        }
      })
      .finally(() => {
        onClose()
      })
  }

  return (
    <GridContainer direction={'column'}>
      <GridItem
        container
        justify={'center'}
        alignItems={'center'}
        xs={12}
        className={classes.newTransportTitle}
      >
        <Typography variant={'h2'} color={'primary'}>
          <Trans>New transport</Trans>
        </Typography>
      </GridItem>
      <GridItem xs={12} container direction={'row'}>
        {isManager && (
          <GridItem
            onClick={setActiveTransportMaterial('RAW')}
            container
            xs={6}
            direction={'column'}
            justify={'center'}
            alignItems={'center'}
            className={cx(
              classes.newTransportMaterial,
              materialType === 'RAW' && classes.newTransportMaterialSelected
            )}
          >
            <Klada />
            <span>
              <Trans>Material</Trans>
            </span>
          </GridItem>
        )}
        <GridItem
          onClick={setActiveTransportMaterial('PROCESSED')}
          container
          xs={6}
          direction={'column'}
          justify={'center'}
          alignItems={'center'}
          className={cx(
            classes.newTransportMaterial,
            materialType === 'PROCESSED' && classes.newTransportMaterialSelected
          )}
        >
          <Stepka />
          <span>
            <Trans>Wood chips</Trans>
          </span>
        </GridItem>
      </GridItem>
      <GridItem xs={12} className={classes.newTransportForm}>
        <Form onSubmit={handleSubmit}>
          {(formProps) => {
            return (
              <form onSubmit={formProps.handleSubmit}>
                <BoxFullWidth p={5}>
                  <GridContainer>
                    <GridItem container xs={12} direction={'column'}>
                      <div className={classes.fieldLabel}>
                        <Trans>Target location</Trans>:
                      </div>
                      <Field
                        name="destination"
                        component={SelectInput}
                        options={locationsList}
                        validate={required}
                        defaultValue={locationsList[0]?.id}
                        className={classes.formField}
                      />
                    </GridItem>
                    <GridItem container xs={7} direction={'column'}>
                      <Box pr={2.5}>
                        <Field
                          label={<Trans>Delivery note:</Trans>}
                          name="delivery_note"
                          component={TextInput}
                          validate={required}
                          placeholder={'C_2021/00234'}
                        />
                      </Box>
                    </GridItem>
                    <GridItem container xs={5} direction={'column'}>
                      <Box pl={2.5}>
                        <Field
                          label={
                            <>
                              <Trans>Netto</Trans>
                              &nbsp;({getMaterialUnits()}):
                            </>
                          }
                          name="netto"
                          type={'number'}
                          component={TextInput}
                          validate={required}
                          className={classes.fieldEndAdornmentPadding}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                className={classes.endAdornmentPadding}
                                position="end"
                              >
                                {getMaterialUnits()}
                              </InputAdornment>
                            ),
                          }}
                          placeholder={'120'}
                        />
                      </Box>
                    </GridItem>
                    <GridItem container justify={'flex-end'} xs={12}>
                      <PrimaryButton
                        type={'submit'}
                        fullWidth={false}
                        formSubmit={true}
                        textPadding={1}
                        size={'small'}
                        text={
                          <GridContainer justify={'center'} alignItems={'center'}>
                            <Trans>Create</Trans>
                          </GridContainer>
                        }
                        disabled={shiftsLoading}
                      />
                    </GridItem>
                  </GridContainer>
                </BoxFullWidth>
              </form>
            )
          }}
        </Form>
      </GridItem>
    </GridContainer>
  )
}

NewTransportDialog.propTypes = {
  classes: PropTypes.object,
  shiftId: PropTypes.number,
  locationsList: PropTypes.array,
  createTransport: PropTypes.func,
  getShifts: PropTypes.func,
  onClose: PropTypes.func,
  locationId: PropTypes.number,
  contractId: PropTypes.number,
}

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      createTransport,
      getShifts,
      getShiftsFromStorage,
    },
    dispatch
  )
}

export default compose(
  withStyles(shiftDetailStyle),
  connect((store) => {
    return {
      locationsList: store.locations.list,
      locationId: store.locations?.detail?.id,
      contractId: store.contracts?.detail?.id,
      currentShift: store.shifts?.currentShift,
      isManager: store.user.isManager,
      shiftsLoading: store.shifts.loading,
    }
  }, mapDispatchToProps)
)(NewTransportDialog)
